import type { CSSProperties } from "react";

import { BLUE_PALETTE, GRAY_PALETTE, GREEN_PALETTE, INDIGO_PALETTE, ORANGE_PALETTE } from "~/styles/palettes";

/**
 * Block palette and type ladder. Everything in a card is 12px or larger; badges are 11px uppercase.
 * Every word a person reads, old values and dates included, is gray.7 or darker: gray.6 fails contrast on
 * white, so tertiary detail steps down by size and placement instead (founder decision 2026-10-28). Amber
 * marks only an open question that blocks saving.
 */
export const BLOCK = {
  ink: INDIGO_PALETTE[9],
  body: GRAY_PALETTE[8],
  secondary: GRAY_PALETTE[7],
  /** Glyphs only, never text. */
  faint: GRAY_PALETTE[5],
  border: GRAY_PALETTE[3],
  divider: GRAY_PALETTE[1],
  footer: GRAY_PALETTE[0],
  chipBg: BLUE_PALETTE[0],
  chipText: BLUE_PALETTE[7],
  /** A picked answer in a question staged in the side pane. */
  chosenBg: BLUE_PALETTE[0],
  savedBg: GREEN_PALETTE[0],
  savedText: GREEN_PALETTE[8],
  warnBg: ORANGE_PALETTE[0],
  warnText: ORANGE_PALETTE[8],
  title: 15,
  bodySize: 13,
  meta: 12,
  badge: 11,
  radius: 12,
} as const;

export const badgeStyle = (tone: "ready" | "saved" | "warn" | "quiet" | "elevated"): CSSProperties => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  fontSize: BLOCK.badge,
  fontWeight: 600,
  letterSpacing: "0.03em",
  textTransform: "uppercase",
  lineHeight: 1.2,
  borderRadius: 4,
  padding: "3px 8px",
  whiteSpace: "nowrap",
  ...(tone === "saved"
    ? { color: BLOCK.savedText, backgroundColor: BLOCK.savedBg }
    : tone === "warn" || tone === "elevated"
      ? { color: BLOCK.warnText, backgroundColor: BLOCK.warnBg }
      : tone === "quiet"
        ? { color: BLOCK.secondary, backgroundColor: BLOCK.divider }
        : { color: BLOCK.chipText, backgroundColor: BLOCK.chipBg }),
});

export const chipStyle = (tone: "neutral" | "elevated" | "new"): CSSProperties => ({
  display: "inline-block",
  fontSize: BLOCK.meta,
  fontWeight: 600,
  lineHeight: 1.2,
  borderRadius: 4,
  padding: "2px 6px",
  textAlign: "center",
  whiteSpace: "nowrap",
  ...(tone === "elevated"
    ? { color: BLOCK.warnText, backgroundColor: BLOCK.warnBg }
    : tone === "new"
      ? { color: BLOCK.savedText, backgroundColor: BLOCK.savedBg }
      : { color: BLOCK.chipText, backgroundColor: BLOCK.chipBg }),
});
